import { InjectionToken } from '@angular/core';

export interface AppRoute {
  path: string;
  title: string;
  description: string;
  icon?: string;
  color?: string;
  keywords?: string[];
  comingSoon?: boolean;
  category?: string;
  inputs?: string[]; // Types of data this tool can accept (e.g., 'pdf', 'image', 'text')
  outputs?: string[]; // Types of data this tool generates
  children?: AppRoute[];
  production?: boolean;
  seoMetadata?: {
    description: string;
    keywords: string[];
  };
}

export const APP_ROUTES_TOKEN = new InjectionToken<AppRoute[]>('APP_ROUTES');

export const TOOL_CATEGORIES = {
  PDF: 'PDF',
  IMAGE: 'Image',
  DIAGRAM: 'Diagram',
  TEXT: 'Text',
  ENCODING: 'Encoding & Security',
  GAMES: 'Games',
} as const;

export const CATEGORY_COLORS: Record<string, string> = {
  [TOOL_CATEGORIES.TEXT]: '#10b981',
  [TOOL_CATEGORIES.PDF]: '#ef4444',
  [TOOL_CATEGORIES.IMAGE]: '#3b82f6',
  [TOOL_CATEGORIES.DIAGRAM]: '#10b981',
  [TOOL_CATEGORIES.ENCODING]: '#4f46e5',
  [TOOL_CATEGORIES.GAMES]: '#f43f5e',
};

export const CORE_APP_ROUTES: AppRoute[] = [
  {
    path: '/pdf',
    title: TOOL_CATEGORIES.PDF,
    description:
      'Professional-grade PDF tools for merging, splitting, and converting documents. Secure, fast, and processed entirely in your browser.',
    icon: 'picture_as_pdf',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.PDF],
    children: [
      {
        path: '/pdf/merge-pdf',
        title: 'Merge PDF Files',
        description: 'Combine multiple PDF files into one single document.',
        keywords: [
          'merge PDF',
          'combine PDF',
          'join PDF',
          'PDF merger',
          'combine PDF online',
          'merge PDF files online',
          'merge PDF free',
          'merge PDF no sign-up',
          'How to merge multiple PDFs into one',
          'ToolCade merge PDF',
        ],
        icon: 'merge',
        color: 'text-red-500',
        inputs: ['pdf'],
        outputs: ['pdf'],
        category: 'Organize & Edit',
        production: true,
      },
      {
        path: '/pdf/split-pdf',
        title: 'Split PDF Pages',
        description: 'Divide PDFs into smaller parts or extract specific pages.',
        keywords: [
          'Split PDF',
          'Separate PDF pages',
          'Extract PDF pages',
          'PDF splitter',
          'Divide PDF',
          'How to split PDF pages online',
          'Split PDF document in half',
          'Extract specific pages from PDF',
          'Secure PDF splitter',
          'Split PDF pages fast',
          'Free tool to separate PDF pages',
          'Split PDF online securely',
          'ToolCade split PDF',
          'ToolCade PDF separator',
        ],
        icon: 'call_split',
        color: 'text-orange-500',
        inputs: ['pdf'],
        outputs: ['pdf'],
        category: 'Organize & Edit',
        production: true,
      },
      {
        path: '/pdf/word-to-pdf',
        title: 'Convert Word to PDF',
        description: 'Convert Microsoft Word documents to professional PDF files.',
        icon: 'description',
        color: 'text-blue-600',
        comingSoon: true,
        category: 'Convert to PDF',
      },
      {
        path: '/pdf/image-to-pdf',
        title: 'Convert Image to PDF',
        description: 'Convert JPG, PNG, and WebP images to high-quality PDFs.',
        keywords: [
          'image to PDF',
          'JPG to PDF',
          'PNG to PDF',
          'photo to PDF',
          'picture to PDF',
          'How to convert JPG to PDF online',
          'Combine multiple images into one PDF',
          'Convert picture to PDF free',
          'Image to PDF converter on mobile',
          'High quality image to PDF',
          'Image to PDF no watermark',
          'Secure photo to PDF converter',
          'Free tool to convert images to PDF',
          'ToolCade image to PDF',
          'ToolCade JPG to PDF',
        ],
        icon: 'image',
        color: 'text-blue-500',
        inputs: ['image'],
        outputs: ['pdf'],
        category: 'Convert to PDF',
        production: true,
      },
      {
        path: '/pdf/pdf-to-img',
        title: 'Convert PDF to Image',
        description: 'Extract pages from PDF as high-quality images.',
        icon: 'collections',
        color: 'text-amber-500',
        comingSoon: true,
        inputs: ['pdf'],
        outputs: ['image'],
        category: 'Convert from PDF',
      },
      {
        path: '/pdf/html-to-pdf',
        title: 'Convert HTML to PDF',
        description: 'Turn web pages or HTML code into printable PDFs.',
        icon: 'html',
        inputs: ['html', 'text'],
        outputs: ['pdf'],
        color: 'text-green-500',
        category: 'Convert to PDF',
      },
      {
        path: '/pdf/organize-pdf',
        title: 'Rearrange PDF Pages',
        description: 'Reorder, rotate, or remove pages from your PDF.',
        icon: 'dashboard_customize',
        color: 'text-purple-500',
        category: 'Organize & Edit',
      },
      {
        path: '/pdf/watermark-pdf',
        title: 'Add PDF Watermark',
        description: 'Stamp an image or text over your PDF pages.',
        icon: 'branding_watermark',
        color: 'text-indigo-400',
        comingSoon: true,
        category: 'Organize & Edit',
      },
      {
        path: '/pdf/page-numbers',
        title: 'Add Page Numbers',
        description: 'Add page numbers or headers/footers to your PDF.',
        icon: 'format_list_numbered',
        color: 'text-slate-400',
        comingSoon: true,
        category: 'Organize & Edit',
      },
      {
        path: '/pdf/protect-pdf',
        title: 'Password Protect PDF',
        description: 'Encrypt your PDF with a strong password.',
        icon: 'lock',
        color: 'text-rose-600',
        comingSoon: true,
        category: 'Security & Privacy',
      },
      {
        path: '/pdf/unlock-pdf',
        title: 'Remove PDF Password',
        description: 'Remove password and restrictions from protected PDFs.',
        icon: 'lock_open',
        color: 'text-emerald-500',
        comingSoon: true,
        category: 'Security & Privacy',
      },
      {
        path: '/pdf/sign-pdf',
        title: 'Digitally Sign PDF',
        description: 'Digitally sign or add hand-drawn signatures to documents.',
        icon: 'draw',
        color: 'text-blue-400',
        comingSoon: true,
        category: 'Security & Privacy',
      },
      {
        path: '/pdf/flatten-pdf',
        title: 'Flatten PDF Forms',
        description: 'Merge form fields and annotations into the page.',
        icon: 'layers_clear',
        color: 'text-slate-600',
        comingSoon: true,
        category: 'Security & Privacy',
      },
      {
        path: '/pdf/compress-pdf',
        title: 'Reduce PDF File Size',
        description: 'Reduce PDF file size while maintaining visual quality.',
        icon: 'compress',
        color: 'text-cyan-500',
        comingSoon: true,
        category: 'Optimize & Repair',
      },
      {
        path: '/pdf/metadata-editor',
        title: 'Edit PDF Metadata',
        description: 'Edit Title, Author, and Keywords in your PDF files.',
        icon: 'edit_note',
        color: 'text-teal-500',
        inputs: ['pdf'],
        outputs: ['pdf'],
        category: 'Optimize & Repair',
      },
      {
        path: '/pdf/repair-pdf',
        title: 'Fix Corrupted PDF',
        description: 'Fix corrupted or unreadable PDF files.',
        icon: 'build',
        color: 'text-amber-600',
        comingSoon: true,
        category: 'Optimize & Repair',
      },
      {
        path: '/pdf/pdf-ocr',
        title: 'Scanned PDF to Text',
        description: 'Turn scanned PDFs into searchable, editable text.',
        icon: 'find_in_page',
        color: 'text-purple-400',
        comingSoon: true,
        category: 'Optimize & Repair',
      },
      {
        path: '/pdf/pdf-parser',
        title: 'Extract Data from PDF',
        description: 'Extract structured data and text from PDF documents.',
        icon: 'code',
        color: 'text-green-400',
        comingSoon: true,
        category: 'Optimize & Repair',
      },
    ],
  },
  {
    path: '/image',
    title: TOOL_CATEGORIES.IMAGE,
    description: 'Blazing fast, browser-native image editing and conversion tools.',
    icon: 'image',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.IMAGE],
    children: [
      {
        path: '/image/image-resizer',
        title: 'Resize Image',
        description: 'Resize images to custom dimensions with aspect ratio lock.',
        keywords: [
          'Image resizer',
          'Resize image online',
          'Photo resizer',
          'Image resizer no quality loss',
          'Resize picture online',
          'ToolCade image resizer',
          'Resize photo',
          'How to resize an image without losing quality',
          'Change photo dimensions online',
          'Resize image for website free',
          'Resize picture for social media',
          'Bulk image resizer',
          'Resize JPG online',
          'Resize PNG free',
          'Image resizer and compressor',
          'Enlarge image without losing quality',
          'Best way to change image dimensions',
          'Fast online photo resizer',
          'compress images online',
          'image compressor online',
          'image compress',
          'ToolCade image resizer',
          'toolcade image compessor',
        ],
        icon: 'aspect_ratio',
        color: 'text-blue-500',
        inputs: ['image'],
        outputs: ['image'],
        category: 'Quick Actions',
        production: true,
      },
      {
        path: '/image/format-converter',
        title: 'Convert Image Format',
        description: 'Convert images between PNG, JPG, and WebP formats instantly.',
        keywords: [
          'Image converter',
          'Convert image format online',
          'JPG to PNG converter',
          'PNG to JPG converter',
          'WebP to JPG converter',
          'Convert image format free',
          'ToolCade image converter',
          'Change image format online',
          'Image format converter no sign-up',
          'Best image format converter',
          'Fast image format conversion online',
          'Photo format changer',
          'PNG to JPG converter online',
          'Convert HEIC to JPG',
          'Free online image converter',
          'Change image extension online',
          'ToolCade image converter',
        ],
        icon: 'transform',
        color: 'text-indigo-500',
        inputs: ['image'],
        outputs: ['image'],
        category: 'Quick Actions',
        production: true,
      },
      {
        path: '/image/base64-encoder',
        title: 'Image to Base64',
        description: 'Convert any image into a Base64 string for embedding.',
        icon: 'code',
        color: 'text-emerald-500',
        inputs: ['image'],
        outputs: ['text'],
        category: 'Quick Actions',
        production: true,
      },
      {
        path: '/image/placeholder-generator',
        title: 'Create Placeholder',
        description: 'Create custom placeholder images for your projects.',
        keywords: [
          'Generate placeholder images for website',
          'Placeholder image generator',
          'Custom placeholder creator',
          'ToolCade placeholder generator',
          'Image placeholder maker',
          'Temporary image generator for developers',
          'ToolCade placeholder generator',
        ],
        icon: 'category',
        color: 'text-amber-500',
        category: 'Quick Actions',
        production: true,
      },
      {
        path: '/image/exif-remover',
        title: 'Remove Photo Metadata',
        description: 'Remove metadata and GPS location from your photos.',
        icon: 'visibility_off',
        color: 'text-rose-500',
        comingSoon: true,
        category: 'Quick Actions',
      },
      {
        path: '/image/image-compressor',
        title: 'Compress Image',
        description: 'Reduce image file size without losing quality.',
        icon: 'compress',
        color: 'text-cyan-500',
        comingSoon: true,
        category: 'Optimization',
      },
      {
        path: '/image/webp-converter',
        title: 'Convert to WebP',
        description: 'Optimized conversion for modern web assets.',
        icon: 'bolt',
        color: 'text-yellow-500',
        comingSoon: true,
        category: 'Optimization',
      },
      {
        path: '/image/smart-cropper',
        title: 'Crop Image',
        description: 'Intelligent aspect ratio cropping for social media.',
        icon: 'crop',
        color: 'text-purple-500',
        comingSoon: true,
        category: 'Optimization',
      },
      {
        path: '/image/background-remover',
        title: 'Remove Background',
        description: 'Automatically remove image backgrounds using AI.',
        icon: 'auto_fix_high',
        color: 'text-indigo-400',
        comingSoon: true,
        category: 'Optimization',
      },
      {
        path: '/image/favicon-generator',
        title: 'Create Favicon',
        description: 'Create favicon packs from any image.',
        icon: 'favorite_border',
        color: 'text-pink-500',
        comingSoon: true,
        category: 'Web Development',
      },
      {
        path: '/image/palette-extractor',
        title: 'Extract Color Palette',
        description: 'Extract dominant colors and hex codes from images.',
        icon: 'palette',
        color: 'text-orange-500',
        comingSoon: true,
        category: 'Web Development',
      },
      {
        path: '/image/image-watermarker',
        title: 'Add Image Watermark',
        description: 'Protect your images with custom text or logo watermarks.',
        icon: 'branding_watermark',
        color: 'text-slate-500',
        comingSoon: true,
        category: 'Web Development',
      },
      {
        path: '/image/svg-optimizer',
        title: 'Optimize SVG',
        description: 'Clean and minify SVG files for web performance.',
        icon: 'cleaning_services',
        color: 'text-green-500',
        comingSoon: true,
        category: 'Web Development',
      },
      {
        path: '/image/meme-generator',
        title: 'Make a Meme',
        description: 'Create hilarious memes from popular templates.',
        icon: 'insert_emoticon',
        color: 'text-yellow-600',
        comingSoon: true,
        category: 'AI & Creative',
      },
      {
        path: '/image/image-ocr',
        title: 'Extract Text from Image',
        description: 'Extract text from images using AI recognition.',
        icon: 'spellcheck',
        color: 'text-blue-400',
        comingSoon: true,
        category: 'AI & Creative',
      },
    ],
  },
  {
    path: '/diagram',
    title: TOOL_CATEGORIES.DIAGRAM,
    description:
      'Visual communication made simple. Generate flowcharts, mind maps, and technical diagrams from text using Mermaid and other local engines.',
    icon: 'schema',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.DIAGRAM],
    children: [
      {
        path: '/diagram/mermaid-editor',
        title: 'Flowchart & Diagram Maker',
        description: 'Generate flowcharts and sequence diagrams from text.',
        icon: 'auto_graph',
        color: 'text-emerald-500',
        comingSoon: true,
        category: 'Visual & Diagrams',
      },
      {
        path: '/diagram/markmap',
        title: 'Markdown Mindmap',
        description: 'Convert Markdown headers into interactive Mind Maps.',
        icon: 'hub',
        color: 'text-amber-500',
        comingSoon: true,
        category: 'Visual & Diagrams',
      },
      {
        path: '/diagram/ascii-flow',
        title: 'Plain Text Diagrams',
        description: 'Create "box and arrow" diagrams using plain text characters.',
        icon: 'border_all',
        color: 'text-slate-500',
        comingSoon: true,
        category: 'Visual & Diagrams',
      },
    ],
  },
  {
    path: '/text',
    title: TOOL_CATEGORIES.TEXT,
    description:
      'Comprehensive text processing utilities for developers. Format, analyze, and transform your data instantly with secure, client-side logic.',
    icon: 'text_snippet',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.TEXT],
    seoMetadata: {
      keywords: ['text converter', 'slugify tool'],
      description: 'a place where you can convert you text based on your preference',
    },
    children: [
      {
        path: '/text/case-converter',
        title: 'Change Text Case',
        description: 'Effortlessly switch between camelCase, snake_case, PascalCase, and more.',
        icon: 'text_fields',
        color: 'text-blue-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Formatting',
        production: true,
        seoMetadata: {
          keywords: [
            'Case Converter',
            'Change Case',
            'Text Case Converter',
            'Online Case Converter',
            'Uppercase to Lowercase',
            'Lowercase to Uppercase',
            'Title Case Converter',
            'camelCase Converter',
            'snake_case Converter',
            'Programmer Case Converter',
          ],
          description: 'Effortlessly switch between camelCase, snake_case, PascalCase, and more.',
        },
      },
      {
        path: '/text/whitespace-manager',
        title: 'Clean Up Spacing',
        description: 'Remove trailing spaces and fix indentation issues.',
        icon: 'space_bar',
        color: 'text-cyan-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Formatting',
        production: true,
        seoMetadata: {
          keywords: [
            'Trim text online',
            'Remove trailing whitespace',
            'Remove leading spaces',
            'Tab to space converter',
            'String cleaner',
            'Normalize whitespace',
            'Fix messy spacing',
            'Remove hidden characters',
            'Remove all spaces from text',
          ],
          description:
            'Remove unnecessary whitespace, convert tabs to spaces, and delete empty lines. The ultimate tool to tidy up code, text, and data formats.',
        },
      },
      {
        path: '/text/duplicate-remover',
        title: 'Remove Duplicate Lines',
        description: 'Clean your lists by removing redundant lines instantly.',
        icon: 'content_copy',
        color: 'text-emerald-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Formatting',
        production: true,
        seoMetadata: {
          keywords: [
            'Remove duplicate lines',
            'Duplicate line remover',
            'Delete duplicate lines',
            'Remove duplicates online',
            'Text deduplicator',
            'Line remover',
          ],
          description:
            'Free Duplicate Line Remover. Easily find and delete repeated lines from text files, email lists, and data sets.',
        },
      },
      {
        path: '/text/diff-checker',
        title: 'Compare Text (Diff)',
        description: 'Compare two text blocks side-by-side to find differences quickly.',
        icon: 'difference',
        color: 'text-purple-500',
        inputs: ['text'],
        category: 'Comparison',
        production: true,
        seoMetadata: {
          keywords: [
            'Diff Checker',
            'Text Compare',
            'Compare Text',
            'Difference Checker',
            'Text Difference Checker',
            'Compare two texts',
            'Diff Tool Online',
            'JSON Diff',
            'XML Diff',
          ],
          description:
            'The easiest way to compare code snippets and text. Find bugs, review changes, and merge differences with our fast, online Diff Tool.',
        },
      },
      {
        path: '/text/find-replace',
        title: 'Search & Replace Text',
        description: 'Search and replace text patterns with regex support.',
        icon: 'find_replace',
        color: 'text-indigo-400',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Comparison',
        seoMetadata: {
          keywords: [
            'Find and Replace Online',
            'Text Replacer',
            'Word Replacer',
            'Replace Text Tool',
            'String Replacer',
            'Change Words in Text',
            'Search and Replace Text',
            'Regex Find and Replace',
            'Regular Expression Tester',
          ],
          description:
            'Instantly find and replace text online. Quickly swap words, characters, or phrases in your documents with our free, secure, and easy-to-use tool.',
        },
      },
      {
        path: '/text/line-sorter',
        title: 'Sort Text Lines',
        description: 'Sort text lines alphabetically or numerically.',
        icon: 'sort_by_alpha',
        color: 'text-slate-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Comparison',
        production: true,
      },
      {
        path: '/text/json-formatter',
        title: 'Prettify JSON',
        description: 'Prettify, minify, and validate your JSON data.',
        icon: 'code',
        color: 'text-amber-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Coding Tools',
        production: true,
      },
      {
        path: '/text/slugify',
        title: 'Create SEO Slugs',
        description: 'Generate SEO-friendly URL slugs for your blogs.',
        icon: 'link',
        color: 'text-rose-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Coding Tools',
        production: true,
      },
      {
        path: '/text/markdown-previewer',
        title: 'Markdown Live Preview',
        description: 'Live GitHub-flavored Markdown preview.',
        icon: 'visibility',
        color: 'text-indigo-500',
        inputs: ['text'],
        outputs: ['html'],
        category: 'Coding Tools',
        production: true,
      },
      {
        path: '/text/base64-codec',
        title: 'Base64 Encode/Decode',
        description: 'Encode or decode text to Base64 format.',
        icon: 'data_object',
        color: 'text-blue-400',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Coding Tools',
      },
      {
        path: '/text/url-codec',
        title: 'URL Encode/Decode',
        description: 'Safe encoding and decoding for URL parameters.',
        icon: 'anchor',
        color: 'text-teal-500',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Coding Tools',
      },
      {
        path: '/text/html-entities',
        title: 'HTML Entity Converter',
        description: 'Convert characters to HTML entities and back.',
        icon: 'html',
        color: 'text-orange-400',
        inputs: ['text'],
        outputs: ['text'],
        category: 'Coding Tools',
      },
      {
        path: '/text/text-statistics',
        title: 'Text Statistics',
        description: 'Detailed analysis of character and word counts.',
        icon: 'analytics',
        color: 'text-pink-500',
        inputs: ['text'],
        category: 'Analysis',
      },
      {
        path: '/text/reading-time',
        title: 'Reading Time Calculator',
        description: 'Estimate how long it takes to read your content.',
        icon: 'schedule',
        color: 'text-cyan-600',
        inputs: ['text'],
        category: 'Analysis',
      },
      {
        path: '/text/lorem-ipsum',
        title: 'Lorem Ipsum Generator',
        description: 'Generate placeholder text for layouts.',
        icon: 'notes',
        color: 'text-amber-600',
        outputs: ['text'],
        category: 'Analysis',
        production: true,
      },
      {
        path: '/text/password-gen',
        title: 'Secure Password Generator',
        description: 'Create secure, random passwords instantly.',
        icon: 'password',
        color: 'text-emerald-600',
        outputs: ['text'],
        category: 'Analysis',
        production: true,
      },
      {
        path: '/text/unit-converter',
        title: 'Scientific Unit Converter',
        description: 'Convert between various units like length, weight, and temperature.',
        icon: 'straighten',
        color: 'text-amber-600',
        comingSoon: true,
        production: false,
        category: 'Analysis',
      },
    ],
  },

  {
    path: '/developer',
    title: TOOL_CATEGORIES.ENCODING,
    description:
      'Professional security and encoding utilities. Decode JWTs, convert punycode, and handle data transformations securely without leaving your device.',
    icon: 'lock',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.ENCODING],
    children: [
      {
        path: '/developer/jwt-decoder',
        title: 'Decode JWT Token',
        description: 'Instant local decoding of JSON Web Tokens.',
        icon: 'vignette',
        color: 'text-indigo-500',
        comingSoon: true,
        category: 'Developer Tools',
      },
      {
        path: '/developer/punycode-converter',
        title: 'Punycode Converter',
        description: 'Convert Internationalized Domain Names (IDN) to ASCII.',
        icon: 'language',
        color: 'text-blue-400',
        comingSoon: true,
        category: 'Developer Tools',
      },
      {
        path: '/developer/unicode-escaper',
        title: 'Unicode Escaper',
        description: 'Transform characters to Unicode escape sequences.',
        icon: 'text_format',
        color: 'text-emerald-500',
        comingSoon: true,
        category: 'Developer Tools',
      },
      {
        path: '/developer/csv-json-converter',
        title: 'Convert CSV to JSON',
        description: 'Bidirectional converter for data cleaning and migration.',
        icon: 'table_view',
        color: 'text-amber-500',
        comingSoon: true,
        category: 'Developer Tools',
      },
      {
        path: '/developer/multi-base-converter',
        title: 'Base Converter',
        description: 'Convert between Binary, Hex, and Base32/58.',
        icon: 'numbers',
        color: 'text-rose-500',
        comingSoon: true,
        category: 'Developer Tools',
      },
      {
        path: '/developer/qr-generator',
        title: 'Create QR Code',
        description: 'Create customizable QR codes for any text or link.',
        icon: 'qr_code_2',
        color: 'text-slate-900',
        comingSoon: true,
        inputs: ['text'],
        outputs: ['image'],
        category: 'Developer Tools',
      },
      {
        path: '/developer/gzip-decompressor',
        title: 'Gzip Inspector',
        description: 'Inspect and decompress Gzip/Zlib payloads.',
        icon: 'folder_zip',
        color: 'text-orange-500',
        comingSoon: true,
        category: 'Developer Tools',
      },
    ],
  },
  {
    path: '/games',
    title: TOOL_CATEGORIES.GAMES,
    description: 'Fun and interactive games to take a break.',
    icon: 'sports_esports',
    color: CATEGORY_COLORS[TOOL_CATEGORIES.GAMES],
    children: [
      {
        path: '/games/2048',
        title: '2048',
        description: 'Join the numbers and get to the 2048 tile!',
        icon: 'grid_view',
        color: 'text-orange-500',
        category: 'Casual',
        production: true,
      },
      {
        path: '/games/tower-building',
        title: 'Stack Towers',
        description: 'Build the tallest tower you can!',
        icon: 'apartment',
        color: 'text-blue-500',
        category: 'Casual',
      },
      {
        path: '/games/typing-test',
        title: 'Speed Typing Test',
        description: 'Test your typing speed and accuracy (WPM).',
        icon: 'keyboard',
        color: 'text-blue-500',
        comingSoon: true,
        category: 'Educational',
      },
      {
        path: '/games/regex-hero',
        title: 'Battle with Regex',
        description: 'Puzzle game using Regular Expressions as attacks.',
        icon: 'terminal',
        color: 'text-purple-500',
        comingSoon: true,
        category: 'Educational',
      },
      {
        path: '/games/snake',
        title: 'Code Snake Game',
        description: 'Arcade classic where the snake eats language logos.',
        icon: 'gesture',
        color: 'text-green-500',
        comingSoon: true,
        category: 'Educational',
      },
      {
        path: '/games/terminal-quest',
        title: 'Terminal RPG',
        description: 'Text-based RPG navigated via Unix commands.',
        icon: 'computer',
        color: 'text-slate-400',
        comingSoon: true,
        category: 'Educational',
      },
      {
        path: '/games/flexbox-defense',
        title: 'Flexbox Defense',
        description: 'Tower defense game positioned using CSS Flexbox.',
        icon: 'view_quilt',
        color: 'text-indigo-500',
        comingSoon: true,
        category: 'Educational',
      },
      {
        path: '/games/memory-match',
        title: 'Icon Memory Game',
        description: 'Card-flipping game using Material UI icons.',
        icon: 'extension',
        color: 'text-rose-500',
        comingSoon: true,
        category: 'Casual',
      },
    ],
  },
];

export const ADMIN_APP_ROUTES: AppRoute[] = [
  {
    path: '/dashboard',
    title: 'Dashboard',
    description: 'Admin Dashboard',
    icon: 'dashboard',
  },
  {
    path: '/blogs',
    title: 'Blogs',
    description: 'Admin Blogs',
    icon: 'blogs',
  },
  {
    path: '/users',
    title: 'Users',
    description: 'Admin Users',
    icon: 'users',
  },
  {
    path: '/roles',
    title: 'Roles',
    description: 'Admin Roles',
    icon: 'roles',
  },
  {
    path: '/permissions',
    title: 'Permissions',
    description: 'Admin Permissions',
    icon: 'permissions',
  },
];

export const ADMIN_APP_ROUTE_PROVIDER = {
  provide: APP_ROUTES_TOKEN,
  useValue: ADMIN_APP_ROUTES,
};

export const CORE_APP_ROUTE_PROVIDER = {
  provide: APP_ROUTES_TOKEN,
  useValue: CORE_APP_ROUTES,
};

export function getAllTools(productionOnly = false): AppRoute[] {
  const tools: AppRoute[] = [];
  CORE_APP_ROUTES.forEach((route) => {
    if (route.children) {
      const filteredChildren = productionOnly
        ? route.children.filter((child) => child.production)
        : route.children;
      tools.push(...filteredChildren);
    }
  });
  return tools;
}
